import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { NotificationProvider } from './context/NotificationContext';
import AppLayout from './components/layout/AppLayout';
import PortalLayout from './components/layout/PortalLayout';
import Login from './pages/Login';
import SetPassword from './pages/SetPassword';
import Portal from './pages/Portal';
import Dashboard from './pages/Dashboard';
import UserManagement from './pages/admin/UserManagement';
import PermissionGroups from './pages/admin/PermissionGroups';
import AuditLog from './pages/admin/AuditLog';
import GroupSiteManagement from './pages/master/GroupSiteManagement';
import SafetyKitManagement from './pages/master/SafetyKitManagement';
import InventoryPage from './pages/inventory/InventoryPage';
import WorkerList from './pages/workers/WorkerList';
import SubcontractorsPage from './pages/subcontractors/SubcontractorsPage';
import RequirementList from './pages/requirements/RequirementList';
import RequirementDetail from './pages/requirements/RequirementDetail';
import TransferList from './pages/transfers/TransferList';
import TransferDetail from './pages/transfers/TransferDetail';
import NotificationList from './pages/notifications/NotificationList';
import StationeryManagement from './pages/stationery/StationeryManagement';
import ITManagement from './pages/it/ITManagement';
import ItemLookup from './pages/itemLookup/ItemLookup';
import SalaryDeductions from './pages/salary/SalaryDeductions';
import SiteHistory from './pages/siteHistory/SiteHistory';
import AssetHistory from './pages/assetHistory/AssetHistory';
import MyProfile from './pages/MyProfile';
import ForceChangePassword from './pages/ForceChangePassword';
import LoadingSpinner from './components/common/LoadingSpinner';

// Which routes this build was compiled with — unset/'all' keeps today's combined
// behavior (one container serving everything), 'portal' or 'materialwatch'
// restricts to a subdomain-specific build (see frontend/Dockerfile's build arg).
const APP_TARGET = process.env.REACT_APP_APP_TARGET;
const SHOW_PORTAL = !APP_TARGET || APP_TARGET === 'portal';
const SHOW_MATERIALWATCH = !APP_TARGET || APP_TARGET === 'materialwatch';

// MaterialWatch has no login page of its own — Portal is the only place
// credentials are ever entered. An unauthenticated visit here (or a stale/
// missing shared cookie) sends the browser to Portal's login via a real
// cross-origin redirect instead of rendering a local form that has no
// backend route to submit to.
const LoginRedirect = () => {
  if (!SHOW_PORTAL && SHOW_MATERIALWATCH) {
    window.location.href = `${process.env.REACT_APP_PORTAL_URL}/login`;
    return <LoadingSpinner />;
  }
  return <Navigate to="/login" replace />;
};

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <LoadingSpinner />;
  if (!user) return <LoginRedirect />;
  return children;
};

const AdminRoute = ({ children }) => {
  const { user } = useAuth();
  if (!user || (user.role !== 'super_admin' && user.role !== 'admin')) return <Navigate to="/" replace />;
  return children;
};

// Allows super_admin (via hasPermission's bypass) OR users who hold at least
// one of the given permissions — 'admin' must actually hold the permission.
const PermissionRoute = ({ permissions, children }) => {
  const { user, hasPermission } = useAuth();
  if (!user) return <LoginRedirect />;
  if (permissions.some((p) => hasPermission(p))) return children;
  return <Navigate to="/" replace />;
};

// Guards routes that belong to a specific module
const ModuleRoute = ({ module, children }) => {
  const { user, hasModuleAccess } = useAuth();
  if (!user) return <LoginRedirect />;
  if (!hasModuleAccess(module)) return <Navigate to="/" replace />;
  return children;
};

// materialwatch-only builds have no Portal launcher — land logged-in users
// straight on their default module page instead.
const MaterialWatchHome = () => {
  const { user, hasPermission } = useAuth();
  if (user?.siteId) return <Navigate to="/inventory" replace />;
  if (hasPermission('dashboard_view')) return <Navigate to="/dashboard" replace />;
  return <Navigate to="/profile" replace />;
};

function AppRoutes() {
  const { user, loading } = useAuth();
  if (loading) return <LoadingSpinner />;
  if (user?.mustChangePassword) return <ForceChangePassword />;

  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/" replace /> : (SHOW_PORTAL ? <Login /> : <LoginRedirect />)} />
      {/* Public — reached from the "set your password" email link, no session required */}
      <Route path="/set-password" element={<SetPassword />} />

      {SHOW_PORTAL && (
        <>
          {/* Portal — standalone, no AppLayout sidebar */}
          <Route path="/" element={<ProtectedRoute><Portal /></ProtectedRoute>} />

          {/* Portal-level pages — PortalLayout (header + back to portal, no sidebar) */}
          <Route path="/" element={<ProtectedRoute><PortalLayout /></ProtectedRoute>}>
            <Route path="users" element={<AdminRoute><UserManagement /></AdminRoute>} />
          </Route>
        </>
      )}

      {SHOW_MATERIALWATCH && (
        <>
          {!SHOW_PORTAL && <Route path="/" element={<ProtectedRoute><MaterialWatchHome /></ProtectedRoute>} />}

          {/* MaterialWatch module — all routes inside AppLayout */}
          <Route path="/" element={<ProtectedRoute><ModuleRoute module="materialwatch"><AppLayout /></ModuleRoute></ProtectedRoute>}>
            <Route path="dashboard" element={<PermissionRoute permissions={['dashboard_view']}><Dashboard /></PermissionRoute>} />
            <Route path="permission-groups" element={<PermissionRoute permissions={['permission_management']}><PermissionGroups /></PermissionRoute>} />
            <Route path="audit-logs" element={<PermissionRoute permissions={['audit_log_view']}><AuditLog /></PermissionRoute>} />
            <Route path="groups" element={<PermissionRoute permissions={['site_group_management']}><GroupSiteManagement /></PermissionRoute>} />
            <Route path="safety-kits" element={<PermissionRoute permissions={['master_data_management']}><SafetyKitManagement /></PermissionRoute>} />
            <Route path="inventory" element={<InventoryPage />} />
            {/* Site Corrections now lives as a tab inside WorkerList, gated
                by its own permission check within that page (a
                worker_management holder who lacks site_correction_review
                still needs the base Workers page to load) — no separate
                route needed. */}
            <Route path="workers" element={<PermissionRoute permissions={['worker_management', 'site_correction_review']}><WorkerList /></PermissionRoute>} />
            <Route path="subcontractors" element={<PermissionRoute permissions={['manage_subcontractors']}><SubcontractorsPage /></PermissionRoute>} />
            <Route path="requirements" element={<PermissionRoute permissions={['requirement_creation','transfer_approval','transfer_request']}><RequirementList /></PermissionRoute>} />
            <Route path="requirements/:id" element={<PermissionRoute permissions={['requirement_creation','transfer_approval','transfer_request']}><RequirementDetail /></PermissionRoute>} />
            {/* inventory_view included so a site-scoped Storekeeper (who no
                longer holds transfer_request) can still reach these pages to
                Mark as Sent / Mark as Received on transfers touching their site —
                the actual button visibility is further scoped by user.siteId. */}
            <Route path="transfers" element={<PermissionRoute permissions={['transfer_request','transfer_approval','inventory_view']}><TransferList /></PermissionRoute>} />
            <Route path="transfers/:id" element={<PermissionRoute permissions={['transfer_request','transfer_approval','inventory_view']}><TransferDetail /></PermissionRoute>} />
            <Route path="notifications" element={<NotificationList />} />
            <Route path="stationery" element={<PermissionRoute permissions={['stationery_manage']}><StationeryManagement /></PermissionRoute>} />
            <Route path="it" element={<PermissionRoute permissions={['it_manage']}><ITManagement /></PermissionRoute>} />
            <Route path="salary-deductions" element={<PermissionRoute permissions={['salary_deduction_view']}><SalaryDeductions /></PermissionRoute>} />
            <Route path="site-history" element={<PermissionRoute permissions={['inventory_view']}><SiteHistory /></PermissionRoute>} />
            <Route path="item-lookup" element={<PermissionRoute permissions={['item_lookup_view']}><ItemLookup /></PermissionRoute>} />
            <Route path="asset-history" element={<PermissionRoute permissions={['asset_history_view']}><AssetHistory /></PermissionRoute>} />
            <Route path="profile" element={<MyProfile />} />
          </Route>
        </>
      )}

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <NotificationProvider>
          <AppRoutes />
        </NotificationProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
